import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import apiService from '../services/apiService';

const DEFAULT_SETTINGS = {
    mqtt_host: '',
    mqtt_port: '1883',
    mqtt_username: '',
    mqtt_password: '',
    mqtt_env: 'prod',
    mqtt_topic_template: '$env$/$station_id$/$device_id$/connector/$connector_id$/cmd/ota',
};

export default function Settings() {
    const { isAdmin } = useAuth();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setMessage({ type: '', text: '' });
            const res = await apiService.getSettings();
            const data = res && res.data !== undefined ? res.data : (res && typeof res === 'object' ? res : null);
            if (data && typeof data === 'object') {
                setSettings({ ...DEFAULT_SETTINGS, ...data });
            }
        } catch (e) {
            setMessage({ type: 'error', text: e.response?.data?.error || e.message || 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
        setMessage({ type: '', text: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAdmin()) {
            setMessage({ type: 'error', text: 'Admin access required' });
            return;
        }
        try {
            setSaving(true);
            setMessage({ type: '', text: '' });
            // Do not send deprecated mqtt_topic_template
            const { mqtt_topic_template, ...toSave } = settings;
            const res = await apiService.updateSettings(toSave);
            if (res.success) {
                setMessage({ type: 'success', text: 'Settings saved. They will apply on next publish.' });
                if (res.data) setSettings((prev) => ({ ...prev, ...res.data }));
            } else {
                setMessage({ type: 'error', text: res.error || 'Failed to save' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: e.response?.data?.error || e.message || 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    if (!isAdmin()) {
        return (
            <div className="max-w-2xl mx-auto py-8">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-amber-800">Only administrators can view and change settings.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">MQTT broker configuration. Topic structure is fixed by the OTA protocol (station-wide, device, connector forms).</p>

            {message.text && (
                <div className={`rounded-lg p-4 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">MQTT host</label>
                        <input
                            type="text"
                            value={settings.mqtt_host}
                            onChange={(e) => handleChange('mqtt_host', e.target.value)}
                            placeholder="mqtt.wattswap.ai"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Hostname only (no mqtts://). Port 8883 uses TLS + MQTT v5.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">MQTT port</label>
                        <input
                            type="text"
                            value={settings.mqtt_port}
                            onChange={(e) => handleChange('mqtt_port', e.target.value)}
                            placeholder="8883"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MQTT username</label>
                    <input
                        type="text"
                        value={settings.mqtt_username}
                        onChange={(e) => handleChange('mqtt_username', e.target.value)}
                        placeholder="Optional"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MQTT password</label>
                    <input
                        type="password"
                        value={settings.mqtt_password}
                        onChange={(e) => handleChange('mqtt_password', e.target.value)}
                        placeholder="Optional"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        autoComplete="new-password"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MQTT env (server fallback)</label>
                    <input
                        type="text"
                        value={settings.mqtt_env || 'prod'}
                        onChange={(e) => handleChange('mqtt_env', e.target.value)}
                        placeholder="prod | staging | dev"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Used only when a publish request omits <code className="bg-gray-100 px-1 rounded">env</code>.
                        Prefer the Prod / Stage / Dev chips on the Publish modal (saved in this browser).
                    </p>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-medium text-amber-900 mb-1">MQTT topic template — deprecated</p>
                    <p className="text-xs text-amber-800 mb-3">
                        Topic structure is now determined automatically per firmware/device/station
                        (station-wide, device-short, and connector forms). This field is no longer applied on publish.
                    </p>
                    <input
                        type="text"
                        value={settings.mqtt_topic_template || ''}
                        readOnly
                        disabled
                        className="w-full border border-amber-200 rounded-md px-3 py-2 font-mono text-sm bg-amber-100/50 text-amber-900 cursor-not-allowed"
                    />
                    <ul className="mt-3 text-xs text-amber-900 space-y-1 font-mono">
                        <li>{'{env}/{station}/cmd/ota'}</li>
                        <li>{'{env}/{station}/{device_id}/cmd/ota'}</li>
                        <li>{'{env}/{station}/{device_id}/connector/{conn}/cmd/ota'}</li>
                    </ul>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save settings'}
                    </button>
                </div>
            </form>
        </div>
    );
}
